import { ClientDetailsSmileyIcon, PacketIcon, PaymentIcons } from 'assets'
import { ClientAvatar } from 'components/card/ClientAvatar'
import { constants, findPermission, getConnectUser, getStorage } from 'helpers'
import { useBindDispatch, useMobilePayment } from 'hooks'
import { getGenderAgeProfile } from 'pages/village/helpers/villageClientHelpers'
import React, { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { ClientStatus, ClientType, Order } from 'storeTypes'
import { InteractionOutcomes, InteractionSources } from 'types'
import './farmerId.scss'

type FarmerIdModalProps = {
    clientData: ClientType
    selectClientHandler: ({
        clientData,
        clientType
    }: {
        clientData: ClientType
        clientType: string
    }) => void
    clientOrders?: Order[]
    closeModalHandler: () => void
    /** When set (e.g. ATC village search), used for all from= navigations. */
    returnPath?: string
    /** Fired when an action leaves the current form. Closing the screen does not. */
    onLeaveForm?: () => void
}

export const FarmerIdModal: FC<FarmerIdModalProps> = ({
    clientData,
    selectClientHandler,
    clientOrders,
    closeModalHandler,
    returnPath,
    onLeaveForm
}) => {
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const fromPath = returnPath || pathname

    const isClientPrevious = (clientData?.all_enrollment || 0) <= 0
    const { username } = getConnectUser()
    const { sendInteractionsActionHandler } = useBindDispatch()
    const { isMobilePaymentEnabled } = useMobilePayment()

    const userPermissions = getStorage(constants.USER_PERMISSIONS)
    const canMakeCardPayment = findPermission(
        userPermissions,
        constants.CAN_MAKE_CARD_PAYMENT
    )

    const farmerModalClickHandler = (callback: () => void) => {
        onLeaveForm?.()
        closeModalHandler()
        selectClientHandler({
            clientData,
            clientType: isClientPrevious ? 'previous' : 'current'
        })

        callback()
    }

    const interactionHandler = (interactionOutcome: InteractionOutcomes) => {
        sendInteractionsActionHandler({
            client_code: clientData.client_code,
            user_id: username,
            outcome: interactionOutcome,
            client_status: clientData.client_status,
            survey_name: constants.TARGET_LIST_SURVEY_NAME,
            survey_version: constants.TARGET_LIST_SURVEY_VERSION,
            village_id: clientData.village_id,
            source: InteractionSources.CONNECT
        })
    }

    const enrollmentClickHandler = () => {
        const updatedClientData = {
            ...clientData,
            form_type: isClientPrevious ? 're-enrollment' : 'modification',
            ...(isClientPrevious
                ? {}
                : {
                      goal_items: clientOrders?.filter(
                          (item) => !item.delivered
                      ),
                      modification: constants.CLIENT_MODIFICATION_TYPES.PACKAGES
                  })
        }
        localStorage.setItem(
            constants.CLIENT_FOR_ENROLLMENT_STORE,
            JSON.stringify(updatedClientData)
        )
        if (isClientPrevious) {
            interactionHandler(InteractionOutcomes.ENROLLED)
        }
        navigate(`/enroll/${clientData.village_id}?from=${fromPath}`)
    }

    const paymentClickHandler = () => {
        localStorage.setItem(
            constants.CLIENT_DETAIL_LOCAL_STORE,
            JSON.stringify(clientData)
        )
        navigate(
            `/payment-selection?from=${fromPath}&clientCode=${
                clientData.client_code
            }&villageId=${clientData.village_id}&clientType=${
                isClientPrevious ? 'previous' : ''
            }&clientStatus=${clientData.client_status || ''}`,
            { replace: true }
        )
    }

    const visitClickHandler = () => {
        const visitData = JSON.stringify({
            clientCode: clientData.client_code,
            clientName: clientData.firstname,
            origin: isClientPrevious ? 'prev_client_detail' : 'client_detail',
            villageId: clientData.village_id
        })
        localStorage.setItem(constants.CLIENT_FOR_VISIT_STORE, visitData)
        navigate(`/visit?from=${fromPath}`, {
            replace: true
        })
    }

    const { t } = useTranslation()

    const genderIconUrl = useMemo(() => {
        return getGenderAgeProfile(t, clientData?.sex, clientData?.age)
    }, [clientData])

    return (
        <div>
            <div
                className="farmer-id--client-data"
                aria-label="client-details-data"
                onClick={() => {
                    farmerModalClickHandler(() =>
                        navigate(
                            `/village/${clientData.village_id}/client/${
                                clientData.client_code
                            }${
                                isClientPrevious ? '/previous' : ''
                            }?from=${fromPath}`,
                            { replace: true }
                        )
                    )
                }}
            >
                <div className="farmer-id--circle">
                    <ClientAvatar
                        genderIconUrl={genderIconUrl}
                        fullname={clientData.fullname}
                        duplicate={clientData.duplicate}
                        size="70"
                    />
                </div>
                <h2 className="farmer-id--name">{clientData.fullname}</h2>
                <p className="farmer-id--client-code">
                    ID: {clientData.client_code}
                </p>
            </div>
            <div className="farmer-id--modal-icons">
                <div className="farmer-id--section-icon">
                    {clientData.client_status !== ClientStatus.FINISHED && (
                        <div
                            className="farmer-id--single-icon"
                            onClick={() => {
                                farmerModalClickHandler(enrollmentClickHandler)
                            }}
                            aria-label="packet-icon-button"
                        >
                            <PacketIcon height="43" />
                        </div>
                    )}
                    {clientData.client_status !== ClientStatus.FINISHED &&
                        (isMobilePaymentEnabled || canMakeCardPayment) && (
                            <div
                                className="farmer-id--single-icon"
                                onClick={() => {
                                    farmerModalClickHandler(paymentClickHandler)
                                }}
                                aria-label="payment-icon-button"
                            >
                                <PaymentIcons />
                            </div>
                        )}
                    <div
                        className="farmer-id--single-icon"
                        onClick={() => {
                            farmerModalClickHandler(visitClickHandler)
                        }}
                        aria-label="client-details-icon-button"
                    >
                        <ClientDetailsSmileyIcon height="43" />
                    </div>
                </div>
            </div>
        </div>
    )
}
